/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useReducer } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  FloatingNotification,
  WixDesignSystemProvider,
} from '@wix/design-system';
import { StatusAlert } from '@wix/wix-ui-icons-common';
import { assignColorToComments } from '../../utils/colorComments';
import {
  isDynamicPage,
  calcCommentInnerPathFromPageSlug,
} from '../../utils/pagesUtils';
import * as Sentry from '@sentry/browser';
import { updateComment } from '../../utils/api';
import {
  VIEW_MODE,
  DOCUMENT_HEADER,
  COMMENT_STATUS,
  EDITOR_TYPE,
} from '../../utils/constants';
import logger from '../../utils/biLogger';
import { scrollTo } from '../SiteRenderer/frameHelpers';
import { user as userFromWindow } from '../../fromWindow';
import SiteRenderProps from './SiteRendererProps';
import CommentsReducer from './reducers/Comments.reducer';
import { DocumentServicesContext } from './DocumentServicesContext';
import { ResponsiveSiteContext } from './ResponsiveSiteContext';
import { ClientContext } from './ClientContext';
import { ViewModeContext } from './ViewModeContext';
import { BreakpointsContext } from './BreakpointsContext';
import HelpBox from '../HelpBox/HelpBox';
import SiteRenderer from '../SiteRenderer/SiteRenderer';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import Header from '../Header/Header';
import DisableTool from '../DisableTool';
import { getFeedbackViewCommentsPageLoaded } from '@wix/bi-logger-get-feedback/v2';
import style from './App.scss';
import '@wix/design-system/styles.global.css';
import { ACTIONS, useAppContext } from './AppContext';

function App({ mockDocumentServices }) {
  const {
    dispatch: appContextDispatch,
    state: { editorType, savedComments, commentId, role, error: disableTool },
  } = useAppContext();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(VIEW_MODE.DESKTOP);
  const [currentBreakPoint, setBreakPoint] = useState({});
  const [shouldShowHelpBox, setShouldShowHelpBox] = useState(false);
  const [documentServices, setDocumentServices] = useState(null);
  const [isSiteResponsive, setSiteIsResponsive] = useState(
    editorType === EDITOR_TYPE.EDITORX || editorType === EDITOR_TYPE.STUDIO,
  );
  const [user, setUser] = useState(userFromWindow || {});
  const [pageId, setPageId] = useState(null);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
  const [isLoadingSite, setIsLoadingSite] = useState(true);
  const [showError, setShowError] = useState(false);
  const [resolvedFilterOn, setResolvedFilterOn] = useState(false);
  const [openFilterOn, setOpenFilterOn] = useState(true);
  const [iframeElement, setIframeElement] = useState(null);
  const [currentPathname, setCurrentPathname] = useState('');
  const documentServicesProviderValue = {
    documentServices,
    setDocumentServices,
    iframeElement,
    setIframeElement,
  };
  const responsiveSiteProviderValue = { isSiteResponsive, setSiteIsResponsive };
  const clientProviderValue = { user, setUser };
  const viewModeProviderValue = { viewMode, setViewMode };
  const breakpointsProviderValue = { currentBreakPoint, setBreakPoint };

  const commentsReducer = new CommentsReducer({
    viewMode,
    currentBreakPoint,
    documentServices,
    pageId,
    isSiteResponsive,
  });
  const [comments, dispatch] = useReducer(
    commentsReducer.reducer.bind(commentsReducer),
    [],
  );

  useEffect(() => {
    if (documentServices) {
      const { relativeUrl, pageAdditionalData } =
        documentServices?.pages?.getRootNavigationInfo() || {};
      if (relativeUrl || pageAdditionalData) {
        setCurrentPathname(relativeUrl || pageAdditionalData);
      }
    }
  }, []);

  const siteRenderProps = new SiteRenderProps(
    dispatch,
    setShowError,
    editorType,
    setUser,
  );

  if (documentServices) {
    if (pageId !== documentServices.pages.getCurrentPageId()) {
      setPageId(documentServices.pages.getCurrentPageId());
    }
  }

  useEffect(() => {
    if (documentServices) {
      setIsLoadingSite(false);
      setShowError(false);
      documentServices.registerToSiteChanged(() => {
        const { relativeUrl, pageAdditionalData } =
          documentServices?.pages?.getRootNavigationInfo();
        const pagePathName = relativeUrl || pageAdditionalData;
        if (pagePathName) {
          if (currentPathname !== pagePathName) {
            setCurrentPathname(pagePathName);
          }
        }
        const currentPageId = documentServices.pages.getCurrentPageId();
        let poolingIndex = 0;
        const checkIfPageRenderByPooling = setInterval(() => {
          if (poolingIndex > 20) {
            clearInterval(checkIfPageRenderByPooling);
            setShowError(true);
            return;
          }
          const isSiteRendered =
            iframeElement.contentDocument.getElementById(currentPageId);
          if (isSiteRendered) {
            clearInterval(checkIfPageRenderByPooling);
            if (currentPageId !== pageId) {
              setPageId(currentPageId);
            }
          }
          poolingIndex++;
        }, 1000);
      });
    }
  }, [documentServices, pageId]);

  useEffect(() => {
    if (commentId) {
      const comment = _.find(savedComments, ({ id }) => {
        return id === commentId;
      });
      if (comment && documentServices) {
        handleOpenComment(comment);
      }
    }
  }, [commentId, documentServices]);

  useEffect(() => {
    if (!showError && documentServices) {
      logger.report(
        getFeedbackViewCommentsPageLoaded({
          editorVersion: editorType,
          user_type: role,
        }),
      );
    }
  }, [documentServices, showError, editorType]);

  useEffect(() => {
    if (_.size(savedComments) > 0) {
      dispatch({
        type: 'SET_COMMENTS',
        comments: assignColorToComments(savedComments),
      });
    }
  }, []);

  function handleSetFilterOpenComments(toggle) {
    setOpenFilterOn(toggle);
  }

  function handleSetFilterResolvedComments(toggle) {
    setResolvedFilterOn(toggle);
  }

  async function changeViewModeAndOpenComment(id, selectedViewMode) {
    if (selectedViewMode !== viewMode) {
      setViewMode(selectedViewMode);
      setIsLoadingSite(true);
      await documentServices.waitForChangesAppliedAsync();
      setIsLoadingSite(false);
      dispatch({ type: 'OPEN_COMMENT', id });
    } else {
      dispatch({ type: 'OPEN_COMMENT', id });
    }
  }
  async function markCommentAsRead(comment) {
    if (comment.status === COMMENT_STATUS.UNREAD) {
      const { id } = comment;
      const changes = { status: COMMENT_STATUS.OPEN };
      const fields = ['status'];
      dispatch({ type: 'UPDATE_COMMENT', id, changes });
      try {
        const { revision } = await updateComment({
          ...comment,
          ...changes,
          fields,
        });
        dispatch({ type: 'UPDATE_COMMENT', id, changes: { revision } });
      } catch (e) {
        setShowError(true);
      }
    }
  }

  async function markCommentAsResolved(comment) {
    if (comment.status !== COMMENT_STATUS.RESOLVED) {
      const { id } = comment;
      const changes = { status: COMMENT_STATUS.RESOLVED };
      const fields = ['status'];
      dispatch({ type: 'UPDATE_COMMENT', id, changes });

      try {
        const { revision } = await updateComment({
          ...comment,
          ...changes,
          fields,
        });
        dispatch({ type: 'UPDATE_COMMENT', id, changes: { revision } });
      } catch (e) {
        setShowError(true);
      }
    }
  }
  async function handleOpenComment(comment) {
    function handleError(error) {
      Sentry.captureException(error);
      appContextDispatch({ type: ACTIONS.SET_SHOW_OVERLAY, payload: false });
      const changes = { location_not_found: true };
      dispatch({ type: 'UPDATE_COMMENT', id, changes });
    }
    const { id, commentLocation } = comment;
    const { breakpoint } = commentLocation;
    dispatch({ type: 'DE_ACTIVATE_COMMENTS' });
    if (
      commentLocation.pageId !== DOCUMENT_HEADER &&
      (commentLocation.pageId !== pageId ||
        !_.get(commentLocation, 'innerPath', '').includes(currentPathname))
    ) {
      const isDynamic = isDynamicPage(commentLocation.pageId, documentServices);
      if (isDynamic === undefined) {
        handleError(`page does not exist ${commentLocation.pageId}`);
        return;
      }
      if (isDynamic) {
        const innerRoute = calcCommentInnerPathFromPageSlug(
          commentLocation,
          documentServices,
        );

        if (!innerRoute) {
          handleError(
            `Dynamic page inner route failed for comment ${commentLocation}`,
          );
          return;
        }
        // comment is in a dynamic page, make sure the inner route is starting from the first slug of the page - and not the site name as for freemium sites
        documentServices.pages.navigateTo({
          type: 'DynamicPageLink',
          innerRoute,
          isTpaRoute: true,
          routerId: commentLocation.pageId,
        });
        await documentServices.waitForChangesAppliedAsync();
      } else {
        documentServices.pages.navigateTo(commentLocation.pageId);
        await documentServices.waitForChangesAppliedAsync();
      }
    }
    if (isSiteResponsive) {
      const { maxWidth, minWidth } = breakpoint;
      if (!comment.isMigrated) {
        setBreakPoint({ max: maxWidth, min: minWidth, id: breakpoint.id });
      }
      dispatch({ type: 'OPEN_COMMENT', id });
    } else {
      await changeViewModeAndOpenComment(id, breakpoint.id);
    }
    markCommentAsRead(comment);
    scrollTo(commentLocation.x, commentLocation.y);
    await documentServices.waitForChangesAppliedAsync();
    appContextDispatch({
      type: ACTIONS.SET_SHOULD_SHOW_COMMENTS_LIST,
      payload: false,
    });
    appContextDispatch({ type: ACTIONS.SET_SHOW_OVERLAY, payload: false });
  }

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <DocumentServicesContext.Provider value={documentServicesProviderValue}>
        <ResponsiveSiteContext.Provider value={responsiveSiteProviderValue}>
          <ViewModeContext.Provider value={viewModeProviderValue}>
            <BreakpointsContext.Provider value={breakpointsProviderValue}>
              <ClientContext.Provider value={clientProviderValue}>
                <div className={style.root}>
                  {disableTool ? <DisableTool /> : <></>}
                  {!disableTool && isLoadingSite ? (
                    <LoadingScreen showLoader={showLoadingOverlay} />
                  ) : null}

                  <Header
                    comments={comments}
                    isLoadingSite={isLoadingSite}
                    closeAllComments={() => {
                      dispatch({ type: 'DE_ACTIVATE_COMMENTS' });
                    }}
                    onDesktopClick={() => setViewMode(VIEW_MODE.DESKTOP)}
                    onMobileClick={() => setViewMode(VIEW_MODE.MOBILE)}
                    onOpenComment={handleOpenComment}
                    onHelpButtonClick={() => setShouldShowHelpBox(true)}
                    onSetFilterOpenComments={toggle =>
                      handleSetFilterOpenComments(toggle)
                    }
                    onSetFilterResolvedComments={toggle =>
                      handleSetFilterResolvedComments(toggle)
                    }
                    resolvedFilterOn={resolvedFilterOn}
                    openFilterOn={openFilterOn}
                  />
                  {showError ? (
                    <FloatingNotification
                      onClose={() => {
                        setShowError(false);
                      }}
                      className={style.errorNotification}
                      type="warning"
                      showTextButton
                      textButtonProps={{
                        label: t('get_feedback_error_cta'),
                        onClick: () => {
                          window.location.reload();
                        },
                      }}
                      prefixIcon={<StatusAlert />}
                      text={t('get_feedback_error')}
                      width="45%"
                    />
                  ) : (
                    ''
                  )}
                  <SiteRenderer
                    style={style.root}
                    mockDocumentServices={mockDocumentServices}
                    comments={comments}
                    pageId={pageId}
                    siteDidNotRender={() => {
                      Sentry.captureException(
                        `failed to load feedback-ng site renderer`,
                      );
                      setShowLoadingOverlay(false);
                      if (!showError) {
                        setShowError(true);
                      }
                    }}
                    resolvedFilterOn={resolvedFilterOn}
                    openFilterOn={openFilterOn}
                    onMarkCommentAsRead={comment => markCommentAsRead(comment)}
                    closeAllComments={() => siteRenderProps.closeAllComments()}
                    onUpdateComment={(comment, changes) =>
                      siteRenderProps.onUpdateComment(comment, changes)
                    }
                    onResolveComment={comment => markCommentAsResolved(comment)}
                    onAppendOwnerReply={(comment, changes) =>
                      siteRenderProps.onAppendOwnerReply(comment, changes)
                    }
                    onDeleteReplyToComment={(comment, reply) => {
                      siteRenderProps.onDeleteReplyToComment(comment, reply);
                    }}
                    onUpdateReplyToComment={(
                      comment,
                      reply,
                      changes,
                      _user,
                    ) => {
                      siteRenderProps.onUpdateReplyToComment(
                        comment,
                        reply,
                        changes,
                        _user,
                      );
                    }}
                  />
                  <HelpBox
                    shouldShowHelpBox={shouldShowHelpBox}
                    onClose={() => setShouldShowHelpBox(false)}
                    editorType={editorType}
                  />
                </div>
              </ClientContext.Provider>
            </BreakpointsContext.Provider>
          </ViewModeContext.Provider>
        </ResponsiveSiteContext.Provider>
      </DocumentServicesContext.Provider>
    </WixDesignSystemProvider>
  );
}

export default App;
